// ─────────────────────────────────────────────────────────────────────────────
// Snapshot · Items Overview — pin persistence (SNAP-IO-4)
//
// Spec calls for server-side persistence keyed to user_id + outlet_id:
//   POST /api/v1/reports/snapshot/pins   { user_id, outlet_id, item_id, action }
//   GET  /api/v1/reports/snapshot/pins?outlet_id=X
//
// That backend does not exist yet, so this module ships a TWO-LAYER design:
//   • localStorage is the ACTIVE store (per user + per outlet) — works today,
//     survives refresh/close, and isolates pins per-user and per-outlet.
//   • The API functions are wired and ready but gated behind PIN_API_ENABLED.
//     Flip the flag (or set REACT_APP_SNAPSHOT_PIN_API=true) once the endpoint
//     is live to migrate to cross-device server persistence with no UI changes.
//
// Pins are keyed by the item's stable `itemId` (from the live item-sales API),
// not by item name — names can repeat across categories, ids can't.
// ─────────────────────────────────────────────────────────────────────────────

import { axiosFetch as authFetch } from './axiosFetch';
import { parseResponse, encryptJson } from './crypto';
import { CREDENTIALS } from '../constants';

/** When true, reads/writes go to the server endpoints; localStorage stays as a mirror. */
export const PIN_API_ENABLED: boolean =
    String(process.env.REACT_APP_SNAPSHOT_PIN_API || '').toLowerCase() === 'true';

const API_BASE = process.env.REACT_APP_API_ENDPOINT_REPORTS || '';
const STORAGE_PREFIX = 'maghil.snapshot.pins';

export type PinAction = 'pin' | 'unpin';

function getToken(passed?: string | null): string | null {
    if (passed && passed.trim()) return passed.trim();
    try {
        const raw = localStorage.getItem(CREDENTIALS);
        if (!raw) return null;
        const stored = JSON.parse(raw);
        const cred = stored?.credentials ?? stored;
        const t = cred?.accessToken ?? cred?.access_token ?? cred?.token ?? cred?.idToken ?? cred?.jwtToken;
        return t && String(t).trim() ? String(t).trim() : null;
    } catch {
        return null;
    }
}

// ── localStorage layer (active) ──────────────────────────────────────────────

function storageKey(userId: string, outletId: string): string {
    return `${STORAGE_PREFIX}::${userId || 'anon'}::${outletId || 'default'}`;
}

export function readLocalPins(userId: string, outletId: string): string[] {
    try {
        const raw = localStorage.getItem(storageKey(userId, outletId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : [];
    } catch {
        return [];
    }
}

export function writeLocalPins(userId: string, outletId: string, names: string[]): void {
    try {
        localStorage.setItem(storageKey(userId, outletId), JSON.stringify(Array.from(new Set(names))));
    } catch {
        /* storage full / disabled — non-fatal */
    }
}

/** Remove all snapshot pin keys from localStorage (called once at app startup). */
export function clearAllLocalPins(): void {
    try {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(STORAGE_PREFIX)) keysToRemove.push(key);
        }
        keysToRemove.forEach((k) => localStorage.removeItem(k));
    } catch {
        /* storage unavailable — non-fatal */
    }
}

// ── Server layer (API-ready, gated) ──────────────────────────────────────────

/** GET pinned item ids for the user+outlet. Returns [] on any failure. */
export async function fetchServerPins(outletId: string, token?: string | null): Promise<string[]> {
    if (!PIN_API_ENABLED) return [];
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const t = getToken(token);
    if (t) headers['Authorization'] = `bearer ${t}`;
    const params = new URLSearchParams({ outlet_id: outletId });
    const res = await authFetch(`${API_BASE}/api/v1/reports/snapshot/pins?${params}`, { headers });
    if (!res.ok) throw new Error('Failed to load pins');
    const text = await res.text();
    const data = parseResponse(text);
    const list = Array.isArray(data) ? data : data?.content ?? data?.pins ?? data?.items ?? [];
    return (list as any[]).map((x) => String(x?.item_id ?? x?.itemId ?? x?.name ?? x)).filter(Boolean);
}

/** POST a single pin / unpin mutation. Throws on failure so callers can retry. */
export async function saveServerPin(
    payload: { userId: string; outletId: string; itemId: string; action: PinAction },
    token?: string | null,
): Promise<void> {
    if (!PIN_API_ENABLED) return;
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const t = getToken(token);
    if (t) headers['Authorization'] = `bearer ${t}`;
    const body = encryptJson({
        user_id: payload.userId,
        outlet_id: payload.outletId,
        item_id: payload.itemId,
        action: payload.action,
    });
    const res = await authFetch(`${API_BASE}/api/v1/reports/snapshot/pins`, { method: 'POST', headers, body });
    if (!res.ok) throw new Error('Failed to save pin');
}

/** Clear all pins for the user+outlet on the server. Best-effort. */
export async function clearServerPins(userId: string, outletId: string, token?: string | null): Promise<void> {
    if (!PIN_API_ENABLED) return;
    const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' };
    const t = getToken(token);
    if (t) headers['Authorization'] = `bearer ${t}`;
    const body = encryptJson({ user_id: userId, outlet_id: outletId, action: 'clear' });
    const res = await authFetch(`${API_BASE}/api/v1/reports/snapshot/pins`, { method: 'POST', headers, body });
    if (!res.ok) throw new Error('Failed to clear pins');
}
